import { Controller } from "@hotwired/stimulus"

// Controller for previewing Janus personas in a modal
export default class extends Controller {
  static targets = ["dialog", "content", "title", "footer", "importBtn", "viewBtn"]
  
  connect() {
    console.log("JanusPreviewController connected", {
      hasDialog: this.hasDialogTarget,
      hasContent: this.hasContentTarget,
      hasFooter: this.hasFooterTarget,
      element: this.element
    })
  }
  
  async open(event) {
    event.preventDefault()
    event.stopPropagation()
    
    console.log("Preview button clicked", event.currentTarget)
    
    const janusId = event.currentTarget.dataset.janusId
    console.log("Janus ID:", janusId)
    
    if (!janusId) {
      console.error("No Janus ID found on button")
      alert("No Janus ID found")
      return
    }
    
    // Find targets within the controller's scope
    const dialog = this.element.querySelector('[data-janus-preview-target="dialog"]')
    const content = this.element.querySelector('[data-janus-preview-target="content"]')
    const footer = this.element.querySelector('[data-janus-preview-target="footer"]')
    const title = this.element.querySelector('[data-janus-preview-target="title"]')
    const importBtn = this.element.querySelector('[data-janus-preview-target="importBtn"]')
    const viewBtn = this.element.querySelector('[data-janus-preview-target="viewBtn"]')
    
    console.log("Found targets:", { dialog, content, footer, title, importBtn, viewBtn })
    
    if (!dialog) {
      console.error("Dialog element not found")
      alert("Dialog element not found. Check console.")
      return
    }
    
    if (!content) {
      console.error("Content element not found")
      alert("Content element not found. Check console.")
      return
    }
    
    // Show loading state
    content.innerHTML = `
      <div class="flex items-center justify-center py-12">
        <div class="text-center">
          <svg class="mx-auto h-12 w-12 text-seris-gray-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          <p class="mt-4 text-sm text-seris-gray-600">Loading persona details...</p>
        </div>
      </div>
    `
    if (footer) footer.style.display = "none"
    
    // Open the dialog using native showModal
    dialog.showModal()
    
    try {
      // Fetch persona details
      const response = await fetch(`/personas/preview_janus_persona?janus_id=${janusId}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      
      if (data.error) {
        this.showError(data.error, { content, footer, importBtn, viewBtn })
        return
      }
      
      // Render persona details
      this.renderPersona(data.persona, data.synced, data.synced_persona_id, { dialog, content, footer, title, importBtn, viewBtn })
      
    } catch (error) {
      console.error("Error loading persona preview:", error)
      this.showError("Failed to load persona details. Please try again.", { content, footer, importBtn, viewBtn })
    }
  }
  
  renderPersona(persona, synced, syncedPersonaId, targets = {}) {
    // Use provided targets or fall back to controller targets
    const dialog = targets.dialog || (this.hasDialogTarget ? this.dialogTarget : null)
    const content = targets.content || (this.hasContentTarget ? this.contentTarget : null)
    const footer = targets.footer || (this.hasFooterTarget ? this.footerTarget : null)
    const title = targets.title || (this.hasTitleTarget ? this.titleTarget : null)
    const importBtn = targets.importBtn || (this.hasImportBtnTarget ? this.importBtnTarget : null)
    const viewBtn = targets.viewBtn || (this.hasViewBtnTarget ? this.viewBtnTarget : null)
    
    if (!content) {
      console.error("Content target not available for rendering")
      return
    }
    const goals = persona.goals || []
    const challenges = persona.challenges || []
    const painPoints = persona.pain_points || []
    
    if (title) title.textContent = persona.name || "Persona Preview"
    
    // Baseball card style - compact and visually appealing
    content.innerHTML = `
      <div class="space-y-4">
        <!-- Header Card -->
        <div class="bg-gradient-to-r from-primary-600 to-primary-700 rounded-t-lg p-4 text-white">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h2 class="text-xl font-bold">${this.escapeHtml(persona.name)}</h2>
              <p class="text-primary-100 text-sm mt-1">${this.escapeHtml(persona.job_title || '')}</p>
            </div>
            ${synced ? `
              <span class="px-2 py-1 inline-flex items-center text-xs font-medium rounded-full bg-green-500 text-white">
                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                Imported
              </span>
            ` : ''}
          </div>
        </div>
        
        <!-- Quick Stats Row -->
        <div class="grid grid-cols-3 gap-3 px-4">
          ${persona.company_size ? `
            <div class="text-center">
              <div class="text-xs text-seris-gray-500 uppercase tracking-wide">Company</div>
              <div class="text-sm font-semibold text-seris-gray-900 mt-1 capitalize">${this.escapeHtml(persona.company_size.replace(/_/g, ' '))}</div>
            </div>
          ` : '<div></div>'}
          ${persona.industry ? `
            <div class="text-center">
              <div class="text-xs text-seris-gray-500 uppercase tracking-wide">Industry</div>
              <div class="text-sm font-semibold text-seris-gray-900 mt-1">${this.escapeHtml(persona.industry)}</div>
            </div>
          ` : '<div></div>'}
          ${persona.role ? `
            <div class="text-center">
              <div class="text-xs text-seris-gray-500 uppercase tracking-wide">Role</div>
              <div class="text-sm font-semibold text-seris-gray-900 mt-1 capitalize">${this.escapeHtml(persona.role.replace(/_/g, ' '))}</div>
            </div>
          ` : '<div></div>'}
        </div>
        
        <!-- Compact Lists in Grid -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 px-4 pb-2">
          ${goals.length > 0 ? `
            <div>
              <h3 class="text-xs font-semibold text-seris-gray-700 uppercase tracking-wide mb-2 flex items-center">
                <svg class="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                Goals
              </h3>
              <ul class="space-y-1.5">
                ${goals.slice(0, 4).map(goal => `
                  <li class="text-xs text-seris-gray-700 line-clamp-2">${this.escapeHtml(goal)}</li>
                `).join('')}
                ${goals.length > 4 ? `<li class="text-xs text-seris-gray-500 italic">+${goals.length - 4} more</li>` : ''}
              </ul>
            </div>
          ` : ''}
          
          ${challenges.length > 0 ? `
            <div>
              <h3 class="text-xs font-semibold text-seris-gray-700 uppercase tracking-wide mb-2 flex items-center">
                <svg class="w-3 h-3 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
                Challenges
              </h3>
              <ul class="space-y-1.5">
                ${challenges.slice(0, 4).map(challenge => `
                  <li class="text-xs text-seris-gray-700 line-clamp-2">${this.escapeHtml(challenge)}</li>
                `).join('')}
                ${challenges.length > 4 ? `<li class="text-xs text-seris-gray-500 italic">+${challenges.length - 4} more</li>` : ''}
              </ul>
            </div>
          ` : ''}
          
          ${painPoints.length > 0 ? `
            <div>
              <h3 class="text-xs font-semibold text-seris-gray-700 uppercase tracking-wide mb-2 flex items-center">
                <svg class="w-3 h-3 text-orange-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                </svg>
                Pain Points
              </h3>
              <ul class="space-y-1.5">
                ${painPoints.slice(0, 4).map(point => `
                  <li class="text-xs text-seris-gray-700 line-clamp-2">${this.escapeHtml(point)}</li>
                `).join('')}
                ${painPoints.length > 4 ? `<li class="text-xs text-seris-gray-500 italic">+${painPoints.length - 4} more</li>` : ''}
              </ul>
            </div>
          ` : ''}
        </div>
        
        ${goals.length === 0 && challenges.length === 0 && painPoints.length === 0 ? `
          <div class="text-center py-6 text-seris-gray-500 text-sm">
            <p>No additional details available for this persona.</p>
          </div>
        ` : ''}
        
        <!-- Footer Metadata -->
        <div class="border-t border-seris-gray-200 pt-3 px-4 pb-2">
          <div class="flex items-center justify-between text-xs text-seris-gray-500">
            <div class="flex items-center gap-4">
              ${persona.id ? `<span>ID: <span class="font-mono font-semibold">${persona.id}</span></span>` : ''}
              ${persona.archived !== undefined ? `
                <span class="px-2 py-0.5 inline-flex text-xs font-medium rounded-full ${persona.archived ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}">
                  ${persona.archived ? 'Archived' : 'Active'}
                </span>
              ` : ''}
            </div>
            ${persona.updated_at ? `<span>Updated ${this.formatDateShort(persona.updated_at)}</span>` : ''}
          </div>
        </div>
      </div>
    `
    
    // Show footer with appropriate action
    if (footer) footer.style.display = "flex"
    if (synced && syncedPersonaId) {
      if (viewBtn) {
        viewBtn.href = `/personas/${syncedPersonaId}`
        viewBtn.style.display = "inline-flex"
      }
      if (importBtn) importBtn.style.display = "none"
    } else {
      if (importBtn) {
        importBtn.dataset.janusId = persona.id
        importBtn.style.display = "inline-flex"
      }
      if (viewBtn) viewBtn.style.display = "none"
    }
  }
  
  showError(message, targets = {}) {
    const content = targets.content || (this.hasContentTarget ? this.contentTarget : null)
    const footer = targets.footer || (this.hasFooterTarget ? this.footerTarget : null)
    const importBtn = targets.importBtn || (this.hasImportBtnTarget ? this.importBtnTarget : null)
    const viewBtn = targets.viewBtn || (this.hasViewBtnTarget ? this.viewBtnTarget : null)
    
    if (!content) {
      console.error("Content target not available for error display")
      return
    }
    
    content.innerHTML = `
      <div class="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>${this.escapeHtml(message)}</p>
            </div>
          </div>
        </div>
      </div>
    `
    if (footer) footer.style.display = "flex"
    if (importBtn) importBtn.style.display = "none"
    if (viewBtn) viewBtn.style.display = "none"
  }
  
  import(event) {
    const janusId = event.currentTarget.dataset.janusId
    if (!janusId) return
    
    // Close dialog first
    this.dialogTarget.close()
    
    // Create and submit form
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/personas/import_from_janus'
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }
    
    const janusIdInput = document.createElement('input')
    janusIdInput.type = 'hidden'
    janusIdInput.name = 'janus_id'
    janusIdInput.value = janusId
    form.appendChild(janusIdInput)
    
    document.body.appendChild(form)
    form.submit()
  }
  
  close() {
    const dialog = this.element.querySelector('[data-janus-preview-target="dialog"]')
    if (dialog) dialog.close()
  }
  
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
  
  formatDate(dateString) {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  formatDateShort(dateString) {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'today'
    if (diffDays === 1) return 'yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
    return `${Math.floor(diffDays / 365)} years ago`
  }
}
